import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Globe, Linkedin, Briefcase, Users, ArrowLeft } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RecruiterProfile() {
  const { hrId } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/public/hr/${hrId}`);
        if (!res.ok) throw new Error('Profile not found');
        const data = await res.json();
        setProfile(data.data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [hrId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#252520] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#c9a84c] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-[#9a9488] uppercase tracking-widest" style={{ fontFamily: 'Arial, sans-serif' }}>
            Loading Profile
          </p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[#252520] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <p className="text-[#e8e6e0] font-semibold mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            Profile not found
          </p>
          <p className="text-sm text-[#9a9488] mb-6">
            This recruiter profile may have been removed or the link is invalid.
          </p>
          <Link to="/" className="text-[#c9a84c] text-sm uppercase tracking-widest hover:underline">
            Visit FashionSpyder
          </Link>
        </div>
      </div>
    );
  }

  const initials = profile.full_name
    ? profile.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="min-h-screen bg-[#252520]" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#1e1e1a', borderBottom: '1px solid #3e3e38' }}>
        <div className="max-w-xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <p style={{
              fontFamily: 'Georgia, "Times New Roman", serif',
              fontSize: 18,
              fontWeight: 700,
              color: '#111',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              backgroundColor: '#f5f4f0',
              padding: '4px 10px',
            }}>
              FashionSpyder
            </p>
          </Link>
          <span style={{ fontSize: 10, color: '#5a5850', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            Talent Intelligence
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-xl mx-auto px-6 py-12">

        {/* Label strip */}
        <div style={{ backgroundColor: '#1e1e1a', padding: '10px 20px', marginBottom: 24 }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#c9a84c', letterSpacing: '0.22em', textTransform: 'uppercase' }}>
            Recruiter Profile
          </p>
        </div>

        {/* Main card */}
        <div style={{ backgroundColor: '#2e2e2a', padding: '32px 36px' }}>

          {/* Avatar + name */}
          <div className="flex items-start gap-5 mb-8">
            <div style={{
              width: 72,
              height: 72,
              backgroundColor: '#363630',
              border: '1px solid #4a4a42',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {profile.company_logo_url ? (
                <img
                  src={profile.company_logo_url}
                  alt={profile.company_name || profile.full_name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 700, color: '#c9a84c' }}>
                  {initials}
                </span>
              )}
            </div>
            <div>
              <p style={{ margin: '0 0 4px', fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 22, fontWeight: 700, color: '#e8e6e0', lineHeight: 1.2 }}>
                {profile.full_name}
              </p>
              {profile.job_title && (
                <p style={{ margin: '0 0 2px', fontSize: 13, color: '#c9a84c', letterSpacing: '0.08em' }}>
                  {profile.job_title}
                </p>
              )}
              {profile.company_name && (
                <p style={{ margin: 0, fontSize: 13, color: '#9a9488' }}>
                  {profile.company_name}
                </p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, backgroundColor: '#3e3e38', marginBottom: 24 }} />

          {/* Links */}
          <div className="flex flex-wrap gap-4 mb-8">
            {profile.company_website && (
              <a
                href={profile.company_website.startsWith('http') ? profile.company_website : `https://${profile.company_website}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#c9a84c', textDecoration: 'none', letterSpacing: '0.06em' }}
              >
                <Globe style={{ width: 14, height: 14 }} strokeWidth={1.5} />
                {profile.company_website.replace(/^https?:\/\//, '')}
              </a>
            )}
            {profile.company_linkedin && (
              <a
                href={profile.company_linkedin.startsWith('http') ? profile.company_linkedin : `https://${profile.company_linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#c9a84c', textDecoration: 'none', letterSpacing: '0.06em' }}
              >
                <Linkedin style={{ width: 14, height: 14 }} strokeWidth={1.5} />
                LinkedIn
              </a>
            )}
          </div>

          {/* Roles hiring for */}
          {(profile.hiring_for || (profile.preferred_roles && profile.preferred_roles.length > 0)) && (
            <>
              <div style={{ marginBottom: 16 }}>
                <p style={{ margin: '0 0 10px', fontSize: 10, fontWeight: 700, color: '#c9a84c', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                  Currently Hiring For
                </p>
                {profile.hiring_for && (
                  <p style={{ margin: '0 0 6px', fontSize: 14, color: '#e8e6e0', lineHeight: 1.6 }}>
                    {profile.hiring_for}
                  </p>
                )}
                {profile.preferred_roles && profile.preferred_roles.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {profile.preferred_roles.map((role, i) => (
                      <span
                        key={i}
                        style={{ fontSize: 11, padding: '3px 10px', backgroundColor: '#363630', border: '1px solid #4a4a42', color: '#9a9488', letterSpacing: '0.06em' }}
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ height: 1, backgroundColor: '#3e3e38', marginBottom: 20 }} />
            </>
          )}

          {/* Locations */}
          {profile.preferred_locations && profile.preferred_locations.length > 0 && (
            <div>
              <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, color: '#c9a84c', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
                Hiring Locations
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {profile.preferred_locations.map((loc, i) => (
                  <span
                    key={i}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#9a9488' }}
                  >
                    <MapPin style={{ width: 11, height: 11 }} strokeWidth={1.5} />
                    {loc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer note */}
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: '#5a5850', lineHeight: 1.8 }}>
            This recruiter is using{' '}
            <a href="https://fashionspyder.com" style={{ color: '#c9a84c', textDecoration: 'none' }}>
              FashionSpyder
            </a>
            {' '}— the talent intelligence platform for fashion professionals.
          </p>
        </div>
      </div>
    </div>
  );
}
